
import { useState, useEffect } from 'react';
import Button from '../../components/base/Button';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Add Schema.org JSON-LD for Contact page
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Kontakt - Dion Bamme",
      "description": "Kontaktieren Sie Dion Bamme für Projekte, Zusammenarbeit oder Fragen",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/contact`,
      "mainEntity": {
        "@type": "Person",
        "name": "Dion Bamme",
        "jobTitle": "Marketing Management Student",
        "email": "dion.bamme@gmail.com",
        "telephone": "+49 176 45725863",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Wernigerode",
          "addressCountry": "DE"
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d3hpsfmdn2fs4irjn510', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white pt-16">
      {/* Hero Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-white to-blue-400 bg-clip-text text-transparent">
            Kontakt
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Haben Sie ein spannendes Projekt im Kopf? Lassen Sie uns darüber sprechen und 
            gemeinsam etwas Großartiges schaffen.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold mb-8">Lassen Sie uns sprechen</h2>
              <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                Ich bin immer offen für neue Herausforderungen und interessante Projekte. 
                Ob Sie eine Idee für eine Web-Anwendung haben, eine mobile App entwickeln 
                möchten oder Unterstützung bei einem bestehenden Projekt benötigen - 
                ich freue mich auf Ihre Nachricht.
              </p>

              <div className="space-y-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-mail-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold">E-Mail</h3>
                    <p className="text-gray-400">dion.bamme@example.com</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-phone-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold">Telefon</h3>
                    <p className="text-gray-400">+49 123 456 7890</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-map-pin-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold">Standort</h3>
                    <p className="text-gray-400">Berlin, Deutschland</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-12">
                <h3 className="text-xl font-semibold mb-6">Folgen Sie mir</h3>
                <div className="flex space-x-4">
                  {[
                    { icon: 'ri-linkedin-fill', url: '#', label: 'LinkedIn' },
                    { icon: 'ri-github-fill', url: '#', label: 'GitHub' },
                    { icon: 'ri-twitter-fill', url: '#', label: 'Twitter' },
                    { icon: 'ri-dribbble-fill', url: '#', label: 'Dribbble' }
                  ].map((social, index) => (
                    <a
                      key={index}
                      href={social.url}
                      className="w-12 h-12 bg-gray-800 hover:bg-blue-600 rounded-lg flex items-center justify-center transition-colors duration-300 cursor-pointer"
                      aria-label={social.label}
                    >
                      <i className={`${social.icon} text-xl text-gray-300 hover:text-white`}></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gray-800 p-8 rounded-lg">
              <h2 className="text-2xl font-bold mb-6">Nachricht senden</h2>
              
              <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white text-sm"
                      placeholder="Ihr Name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      E-Mail *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white text-sm"
                      placeholder="ihre.email@example.com"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="subject" className="block text-sm font-medium mb-2">
                    Betreff *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white text-sm"
                    placeholder="Worum geht es?"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium mb-2">
                    Nachricht *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white text-sm resize-none"
                    placeholder="Erzählen Sie mir von Ihrem Projekt..."
                  ></textarea>
                  <div className="text-right text-xs text-gray-400 mt-1">
                    {formData.message.length}/500 Zeichen
                  </div>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 flex items-center justify-center mr-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                      </div>
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      Nachricht senden
                      <div className="w-4 h-4 flex items-center justify-center ml-2">
                        <i className="ri-send-plane-line"></i>
                      </div>
                    </>
                  )}
                </Button>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="mt-4 p-4 bg-green-600/20 border border-green-600/30 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-5 h-5 flex items-center justify-center mr-2">
                        <i className="ri-check-line text-green-400"></i>
                      </div>
                      <span className="text-green-400">Nachricht erfolgreich gesendet!</span>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mt-4 p-4 bg-red-600/20 border border-red-600/30 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-5 h-5 flex items-center justify-center mr-2">
                        <i className="ri-error-warning-line text-red-400"></i>
                      </div>
                      <span className="text-red-400">Fehler beim Senden. Bitte versuchen Sie es erneut.</span>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-16">Häufige Fragen</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                question: 'Wie lange dauert ein typisches Projekt?',
                answer: 'Die Projektdauer variiert je nach Umfang und Komplexität. Einfache Websites dauern 2-4 Wochen, während komplexere Anwendungen 2-6 Monate benötigen können.'
              },
              {
                question: 'Welche Technologien verwenden Sie?',
                answer: 'Ich arbeite hauptsächlich mit modernen Technologien wie React, Vue.js, Node.js, Python und verschiedenen Datenbanken. Die Auswahl hängt von den Projektanforderungen ab.'
              },
              {
                question: 'Bieten Sie auch Wartung und Support an?',
                answer: 'Ja, ich biete langfristige Wartung und Support für alle entwickelten Projekte an. Dies umfasst Updates, Bugfixes und neue Features.'
              },
              {
                question: 'Wie läuft die Zusammenarbeit ab?',
                answer: 'Wir beginnen mit einem ausführlichen Gespräch über Ihre Anforderungen, erstellen dann ein Konzept und arbeiten in iterativen Zyklen mit regelmäßigem Feedback.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-gray-900 p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-blue-400">{faq.question}</h3>
                <p className="text-gray-300">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
